import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import Header from '../components/Header';
import MrtConfirmDialog from '../components/MrtConfirmDialog';
import MrtDialog from '../components/MrtDialog';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

function toInt(v) {
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
}

function buildParentOf(childrenOf) {
  const parentOf = {};
  const obj = (childrenOf && typeof childrenOf === 'object') ? childrenOf : {};
  Object.keys(obj).forEach((p) => {
    const pInt = toInt(p);
    const kids = Array.isArray(obj[p]) ? obj[p] : [];
    kids.forEach((c) => {
      const cInt = toInt(c);
      if (pInt !== null && cInt !== null && typeof parentOf[cInt] === 'undefined') parentOf[cInt] = pInt;
    });
  });
  return parentOf;
}

function collectAncestors(id, parentOf) {
  const out = [];
  let cur = id;
  let guard = 0;
  while (cur && guard++ < 2000) {
    const p = parentOf[cur];
    if (!p) break;
    out.push(p);
    cur = p;
  }
  return out;
}

function buildNodePathNames(id, parentOf, getNode) {
  // root -> ... -> id
  const chain = [id, ...collectAncestors(id, parentOf)].reverse();
  const names = [];
  for (let i = 0; i < chain.length; i += 1) {
    const n = getNode(chain[i]);
    const nm = String(n?.name || '').trim();
    if (nm) names.push(nm);
  }
  return names;
}

function NodeRow({ node, level, hasChildren, expanded, onToggle, muted, highlight }) {
  const pad = 8 + (level * 12);
  // ✅ solar_node.userid(=uid)로 조인된 solar_member.id(로그인 id)를 표시
  // - 백엔드 tree API에서 `userid_login_id`로 내려줌 (LEFT JOIN solar_member ON solar_member.uid = solar_node.userid)
  // - uid(hex)로 fallback하지 않음 (요청: uid가 아니라 id를 보여야 함)
  const loginId = (String(node?.userid_login_id || '').trim() || '-');
  const plantCount = Number(node?.plant_count ?? 0);
  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          try { e.preventDefault(); } catch (_e) { /* ignore */ }
          try { node?.__onPick?.(); } catch (_e) { /* ignore */ }
        }
      }}
      onClick={() => { try { node?.__onPick?.(); } catch (_e) { /* ignore */ } }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '2px 6px',
        paddingLeft: `${pad}px`,
        borderRadius: '10px',
        border: '1px solid rgba(15,23,42,0.08)',
        background: highlight
          ? 'rgba(37, 99, 235, 0.10)'
          : (muted ? 'rgba(148,163,184,0.06)' : 'rgba(2,6,23,0.02)'),
        cursor: 'pointer',
        userSelect: 'none',
      }}
      title={String(node?.__pickTitle || '').trim() || undefined}
    >
      <button
        type="button"
        onClick={hasChildren ? ((e) => { try { e.stopPropagation(); } catch (_e) { /* ignore */ } onToggle?.(); }) : undefined}
        disabled={!hasChildren}
        style={{
          width: '18px',
          height: '18px',
          borderRadius: '6px',
          border: '1px solid rgba(15,23,42,0.14)',
          background: hasChildren ? 'var(--mrt-surface)' : 'rgba(148,163,184,0.12)',
          cursor: hasChildren ? 'pointer' : 'default',
          fontWeight: 900,
          color: 'var(--mrt-text-2)',
          padding: 0,
          lineHeight: 1,
          fontSize: '12px',
        }}
        title={hasChildren ? (expanded ? '접기' : '펼치기') : '하위 없음'}
      >
        {hasChildren ? (expanded ? '▾' : '▸') : ''}
      </button>
      <div style={{ width: '14px', textAlign: 'center', fontSize: '12px', lineHeight: 1 }}>
        {hasChildren ? (expanded ? '📂' : '📁') : '📁'}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 950, color: 'var(--mrt-text)', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {String(node?.name || '')}
          <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 800, color: 'var(--mrt-muted)' }}>
            (ID {String(loginId)} · 총관리사이트 {Number.isFinite(plantCount) ? plantCount.toLocaleString() : 0}개)
          </span>
        </div>
      </div>
      {node?.__showAdd ? (
        <button
          type="button"
          onClick={(e) => {
            try { e.stopPropagation(); } catch (_e) { /* ignore */ }
            try { node?.__onAddChild?.(); } catch (_e) { /* ignore */ }
          }}
          style={{
            height: '22px',
            width: '22px',
            borderRadius: '8px',
            border: '1px solid var(--mrt-border)',
            background: 'var(--mrt-surface)',
            cursor: 'pointer',
            fontWeight: 950,
            color: 'var(--mrt-text-2)',
            flex: '0 0 auto',
          }}
          title="하위 그룹 추가"
        >
          +
        </button>
      ) : null}
    </div>
  );
}

export default function GroupTree({
  embedded = false,
  onClose = null,
  selectMode = false,
  onSelectNode = null,
  // ✅ selectMode에서 특정 노드 선택을 막고 싶을 때 사용(예: 멀티에서 최상위 MRT 선택 금지)
  disallowSelectMrtRoot = false,
}) {
  const apiUrl = getApiBaseUrl();
  const scrollBoxRef = useRef(null);
  const nodeElByIdRef = useRef(new Map()); // id -> HTMLElement
  const lastScrollKeyRef = useRef('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tree, setTree] = useState({ nodes: {}, children_of: {}, roots: [], count: 0 });
  // ✅ 검색: 입력은 uncontrolled(ref)로 두고(타이핑 리렌더 방지), 적용값(qApplied)만 state로 관리(엔터 시 적용)
  const qInputRef = useRef(null);
  const [qApplied, setQApplied] = useState('');
  // ✅ userExpanded: 사용자가 직접 펼친 상태만 저장 (검색 자동확장/경로확장과 분리)
  const [userExpanded, setUserExpanded] = useState(() => new Set());
  const deferredQ = useDeferredValue(qApplied);
  const [rootRenderLimit, setRootRenderLimit] = useState(80); // ✅ 루트부터 점진 렌더(성능)
  // ✅ 그룹 이동
  const [selectedSourceSeq, setSelectedSourceSeq] = useState(null);
  const [moveMode, setMoveMode] = useState(false);
  const [moveTargetSeq, setMoveTargetSeq] = useState(null);
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const [moveWorking, setMoveWorking] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [moveSuccess, setMoveSuccess] = useState('');
  // ✅ 그룹 추가
  const [addOpen, setAddOpen] = useState(false);
  const [addParentSeq, setAddParentSeq] = useState(null);
  const [addParentLabel, setAddParentLabel] = useState('');
  const [addWorking, setAddWorking] = useState(false);
  const [addError, setAddError] = useState('');
  // ✅ 입력 타이핑 성능: 모달 입력값은 state로 두면 트리 전체가 매 타이핑마다 리렌더 → ref(uncontrolled)로 관리
  const addNameRef = useRef('');
  const addInputRef = useRef(null);
  const [addInputKey, setAddInputKey] = useState(0);
  // ✅ 중복확인 버튼 상태
  const [addDupChecked, setAddDupChecked] = useState(false);
  const [addDupOk, setAddDupOk] = useState(false);
  const [addDupWorking, setAddDupWorking] = useState(false);
  const [addDupMsg, setAddDupMsg] = useState('');
  // ✅ 검색 결과 순환 선택(엔터)
  const [activeHitId, setActiveHitId] = useState(null);
  const [hitCursor, setHitCursor] = useState(0);
  // ✅ 같은 검색어/같은 결과여도 Enter를 누르면 다시 "찾기(스크롤)" 되도록 강제 트리거
  const [hitScrollTick, setHitScrollTick] = useState(0);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const url = `${apiUrl || ''}/api/solar_node/tree?_=${Date.now()}`;
      const res = await fetch(url, { method: 'GET' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `tree_fetch_failed:${res.status}`);
      if (!data?.success) throw new Error(data?.detail || 'tree_fetch_failed');
      setTree({
        nodes: data?.nodes || {},
        children_of: data?.children_of || {},
        roots: data?.roots || [],
        count: data?.count || 0,
      });
    } catch (e) {
      setTree({ nodes: {}, children_of: {}, roots: [], count: 0 });
      setError(String(e?.message || e || 'tree_fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
 
  useEffect(() => {
    // qApplied가 바뀌었을 때 입력창에 반영(예: 검색 버튼/엔터로 적용 후 UI 동기화)
    try {
      if (qInputRef.current) qInputRef.current.value = String(qApplied || '');
    } catch (e) { /* ignore */ }
  }, [qApplied]);

  useEffect(() => {
    // 트리 갱신 시 선택이 유효하지 않으면 정리
    const nodes = tree?.nodes || {};
    if (selectedSourceSeq != null && !nodes[String(selectedSourceSeq)] && !nodes[selectedSourceSeq]) {
      setSelectedSourceSeq(null);
      setMoveMode(false);
      setMoveTargetSeq(null);
    }
    if (moveTargetSeq != null && !nodes[String(moveTargetSeq)] && !nodes[moveTargetSeq]) {
      setMoveTargetSeq(null);
    }
  }, [tree?.nodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const parentOf = useMemo(() => buildParentOf(tree?.children_of), [tree?.children_of]);

  const queryLower = useMemo(() => String(deferredQ || '').trim().toLowerCase(), [deferredQ]);

  const directMatchSet = useMemo(() => {
    // ✅ "직접 매칭"된 그룹만(조상 경로 포함 X) 하이라이트/표시 기준으로 사용
    if (!queryLower) return null;
    const nodes = tree?.nodes || {};
    const hit = new Set();
    const qNum = /^\d+$/.test(queryLower) ? queryLower : null;
    Object.keys(nodes).forEach((k) => {
      const n = nodes[k] || {};
      const name = String(n.name || '').toLowerCase();
      const seqStr = String(n.seq ?? k ?? '').toLowerCase();
      const matched =
        name.includes(queryLower) ||
        (qNum ? seqStr.includes(qNum) : false);
      if (matched) {
        const id = toInt(n.seq ?? k);
        if (id !== null) hit.add(id);
      }
    });
    return hit;
  }, [queryLower, tree?.nodes]);
 
  const hitIds = useMemo(() => {
    const s = directMatchSet;
    if (!s || !s.size) return [];
    return Array.from(s.values()).map(toInt).filter((x) => x !== null).sort((a, b) => a - b);
  }, [directMatchSet]);
 
  useEffect(() => {
    // 검색어가 적용되면 첫 번째 결과를 기본 선택
    if (!String(qApplied || '').trim()) {
      setActiveHitId(null);
      setHitCursor(0);
      return;
    }
    if (!hitIds.length) {
      setActiveHitId(null);
      setHitCursor(0);
      return;
    }
    setActiveHitId(hitIds[0]);
    setHitCursor(0);
  }, [qApplied, hitIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterSet = useMemo(() => {
    if (!queryLower) return null;
    const hit = new Set();
    const direct = (directMatchSet && directMatchSet.size) ? directMatchSet : null;
    if (!direct) return null;
    direct.forEach((id) => {
      hit.add(id);
      collectAncestors(id, parentOf).forEach((a) => hit.add(a));
    });
    return hit;
  }, [queryLower, tree?.nodes, parentOf, directMatchSet]);

  const effectiveFilterSet = useMemo(() => {
    // ✅ 검색은 "그룹명 / 그룹ID(seq)"만 지원
    if (filterSet && filterSet.size) return filterSet;
    return null;
  }, [filterSet]);

  const roots = useMemo(() => {
    const r = Array.isArray(tree?.roots) ? tree.roots : [];
    return r.map((x) => toInt(x)).filter((x) => x !== null);
  }, [tree?.roots]);

  const childrenOf = useMemo(() => {
    const obj = (tree?.children_of && typeof tree.children_of === 'object') ? tree.children_of : {};
    const out = {};
    Object.keys(obj).forEach((k) => { out[toInt(k)] = (Array.isArray(obj[k]) ? obj[k].map(toInt).filter((x) => x !== null) : []); });
    return out;
  }, [tree?.children_of]);

  const getNode = (id) => {
    const nodes = tree?.nodes || {};
    return nodes[String(id)] || nodes[id] || { seq: id, name: String(id) };
  };
 
  const getNodePath = (id) => {
    try {
      const names = buildNodePathNames(id, parentOf, getNode);
      return names.join(' > ');
    } catch (e) {
      return '';
    }
  };
 
  const getNodeLabel = (id) => {
    const n = getNode(id);
    const nm = String(n?.name || '').trim() || String(id);
    return `${nm} (#${String(id)})`;
  };

  const visibleRoots = useMemo(() => {
    if (!effectiveFilterSet) return roots;
    return roots.filter((r) => effectiveFilterSet.has(r));
  }, [roots, effectiveFilterSet]);

  const normalRoots = visibleRoots;

  const isSearchMode = useMemo(() => {
    return !!(String(qApplied || '').trim());
  }, [qApplied]);

  useEffect(() => {
    // 검색 중에는 결과가 보이도록 제한 해제, 평상시엔 루트만 점진 렌더
    if (isSearchMode) setRootRenderLimit(999999);
    else setRootRenderLimit(80);
  }, [isSearchMode]);

  const autoExpanded = useMemo(() => {
    // ✅ 검색 중에는 "경로가 보이도록" 필요한 부모만 자동 펼침(초기 세팅용)
    if (!effectiveFilterSet) return new Set();
    const next = new Set();
    Object.keys(childrenOf || {}).forEach((k) => {
      const p = toInt(k);
      if (p === null) return;
      if (!effectiveFilterSet.has(p)) return;
      const kids = childrenOf[p] || [];
      const hasChildInSet = kids.some((c) => effectiveFilterSet.has(c));
      if (hasChildInSet) next.add(p);
    });
    return next;
  }, [childrenOf, effectiveFilterSet]);

  const effectiveExpanded = useMemo(() => {
    // ✅ 실제 렌더에서 사용할 확장 상태 = 사용자 확장 + 자동 경로 확장 + 계정 경로 확장
    const next = new Set(userExpanded || []);
    autoExpanded.forEach((x) => next.add(x));
    return next;
  }, [userExpanded, autoExpanded]);

  useEffect(() => {
    // ✅ 검색 결과(선택된 activeHitId)로 스크롤
    const target = toInt(activeHitId);
    if (target === null) return;
    const key = `group-hit:${String(qApplied || '').trim().toLowerCase()}:${target}:${hitCursor}:${hitScrollTick}`;
    if (lastScrollKeyRef.current === key) return;

    const tryScroll = (attempt) => {
      const box = scrollBoxRef.current;
      const el = nodeElByIdRef.current.get(target);
      if (!box || !el) {
        if (attempt < 12) requestAnimationFrame(() => tryScroll(attempt + 1));
        return;
      }
      try {
        const boxRect = box.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta = (elRect.top - boxRect.top) - (box.clientHeight / 2) + (elRect.height / 2);
        box.scrollTop = Math.max(0, box.scrollTop + delta);
        lastScrollKeyRef.current = key;
      } catch (e) {
        try { el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' }); } catch (_e) {}
        lastScrollKeyRef.current = key;
      }
    };
    requestAnimationFrame(() => tryScroll(0));
  }, [activeHitId, hitCursor, hitScrollTick, qApplied, effectiveExpanded]);

  const toggle = (id) => {
    setUserExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAllVisible = () => {
    if (isSearchMode) return; // 검색 중 전체 펼치기는 렌더 폭발 위험이 커서 방지
    const next = new Set();
    const visit = (id) => {
      const kids = childrenOf[id] || [];
      if (kids.length) next.add(id);
      kids.forEach(visit);
    };
    visibleRoots.forEach(visit);
    setUserExpanded(next);
  };

  const collapseAll = () => setUserExpanded(new Set());

  useEffect(() => {
    // ✅ 루트(1차) 그룹이 소스로 선택되어 "그룹 이동 상태"가 되는 것을 방지(잔상 제거)
    if (selectMode) return;
    const cur = toInt(selectedSourceSeq);
    if (cur == null) return;
    if (Array.isArray(roots) && roots.includes(cur)) {
      setSelectedSourceSeq(null);
      setMoveMode(false);
      setMoveTargetSeq(null);
    }
  }, [selectMode, roots, selectedSourceSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  const listHeight = useMemo(() => {
    // 화면 높이에 따라 자동으로(최소/최대) 조절
    const h = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 900;
    return Math.max(340, Math.min(780, h - 260));
  }, []);

  const renderTreeNode = (id, level, forced) => {
    const inScope = (!effectiveFilterSet) || effectiveFilterSet.has(id) || !!forced;
    if (!inScope) return null;

    const node = getNode(id);
    const kids = childrenOf[id] || [];
    const hasChildren = kids.length > 0;
    const isExpanded = effectiveExpanded.has(id);
    const muted = !!effectiveFilterSet && !effectiveFilterSet.has(id);
    const isHit = (!!directMatchSet && directMatchSet.has(id));
    const isActiveHit = (toInt(activeHitId) !== null) && (id === toInt(activeHitId));
    const highlight = isActiveHit || isHit;
    const isSelectedSource = (selectedSourceSeq != null) && (id === toInt(selectedSourceSeq));
    const isSelectedTarget = moveMode && (moveTargetSeq != null) && (id === toInt(moveTargetSeq));
    const isUserExpanded = !!(userExpanded && userExpanded.has(id));
 
    // ✅ "멀티는 최상위 1차 그룹(MRT) 선택 금지" 규칙용
    const normName = String(node?.name || '').trim().replace(/\s+/g, '').toUpperCase();
    const isMrtRoot = (level === 0) && (normName === 'MRT');
    const isRootLevel = (level === 0);
    // ✅ 요청사항: 1차(루트) 그룹은 선택 모드에서 선택 불가
    const isBlockedSelect = !!(selectMode && (isRootLevel || (disallowSelectMrtRoot && isMrtRoot)));

    const onPick = () => {
      setMoveError('');
      setMoveSuccess('');
      if (selectMode) {
        if (isBlockedSelect) {
          setMoveError('1차(부모) 그룹은 선택할 수 없습니다.');
          return;
        }
        setSelectedSourceSeq(id);
        return;
      }
      if (moveMode) {
        // 이동 모드: 대상 선택
        if (selectedSourceSeq != null && id === toInt(selectedSourceSeq)) return;
        setMoveTargetSeq(id);
      } else {
        // 기본: 이동할 소스 선택
        // ✅ 1차(루트) 그룹은 이동할 그룹(소스)로 선택 불가
        if (isRootLevel) {
          setMoveError('1차(부모) 그룹은 이동할 그룹으로 선택할 수 없습니다.');
          return;
        }
        setSelectedSourceSeq(id);
      }
    };

    const setRef = (el) => {
      if (el) nodeElByIdRef.current.set(id, el);
      else nodeElByIdRef.current.delete(id);
    };

    return (
      <div
        key={String(id)}
        ref={setRef}
        style={{
          borderRadius: '12px',
          border: isSelectedTarget
            ? '1px solid rgba(16, 185, 129, 0.55)'
            : (isSelectedSource
              ? '1px solid rgba(245, 158, 11, 0.65)'
              : (isActiveHit ? '2px solid rgba(37, 99, 235, 0.70)' : (highlight ? '1px solid rgba(37, 99, 235, 0.40)' : '1px solid var(--mrt-border)'))),
          background: isSelectedTarget
            ? 'rgba(16, 185, 129, 0.06)'
            : (isSelectedSource
              ? 'rgba(245, 158, 11, 0.08)'
              : (isActiveHit ? 'rgba(37, 99, 235, 0.10)' : (highlight ? 'rgba(37, 99, 235, 0.04)' : 'var(--mrt-surface)'))),
          padding: '1px',
          display: 'grid',
          gap: '4px',
          // ✅ 화면 밖은 렌더를 브라우저가 최대한 늦추도록 힌트
          // - 검색 중에도 'auto'를 유지해야 스크롤/클릭 버벅임이 줄어듦
          contentVisibility: 'auto',
          containIntrinsicSize: '84px',
        }}
      >
        <NodeRow
          node={{
            ...(node || {}),
            __onPick: isBlockedSelect ? null : onPick,
            __showAdd: (!selectMode && !moveMode),
            __onAddChild: () => {
              setAddError('');
              addNameRef.current = '';
              setAddParentSeq(id);
              setAddParentLabel(getNodeLabel(id));
              setAddInputKey((k) => k + 1);
              setAddDupChecked(false);
              setAddDupOk(false);
              setAddDupWorking(false);
              setAddDupMsg('');
              setAddOpen(true);
            },
            __pickTitle: moveMode
              ? `대상 그룹으로 선택: ${getNodeLabel(id)}`
              : (selectMode
                ? (isBlockedSelect ? '선택 불가(1차 그룹 선택 금지)' : `선택: ${getNodeLabel(id)}`)
                : `이동할 그룹 선택: ${getNodeLabel(id)}`),
          }}
          level={level}
          hasChildren={hasChildren}
          expanded={isExpanded}
          onToggle={() => toggle(id)}
          muted={muted}
          highlight={highlight}
        />
        {hasChildren && isExpanded ? (
          <div
            style={{
              marginLeft: '6px',
              paddingLeft: '6px',
              borderLeft: '2px solid rgba(148,163,184,0.25)',
              display: 'grid',
              gap: '4px',
            }}
          >
            {/* ✅ 검색 중에는 자동 확장 노드가 전체 하위 트리를 렌더링하지 않도록 제한
                - 사용자가 직접 펼친 노드(userExpanded) 또는 forced=true(부모가 직접 펼쳐 전체 보기 상태)일 때만 전체 렌더
                - 그 외에는 effectiveFilterSet(경로/매칭)에 포함된 자식만 렌더 */}
            {(() => {
              // ✅ 핵심: 검색 모드에서는 forced=true를 하위로 무조건 전파하면 트리가 통째로 렌더되어 버벅임 발생
              // - 검색 모드: 기본은 필터에 걸린 자식만 렌더, 단 사용자가 직접 펼친 노드는 전체 보기 허용
              const childForced = isSearchMode ? isUserExpanded : true;
              if (isSearchMode && !(childForced || forced)) {
                return kids
                  .filter((c) => (effectiveFilterSet ? effectiveFilterSet.has(c) : false))
                  .map((c) => renderTreeNode(c, level + 1, childForced));
              }
              return kids.map((c) => renderTreeNode(c, level + 1, childForced));
            })()}
          </div>
        ) : null}
      </div>
    );
  };

  const rootsToRender = useMemo(() => {
    const list = Array.isArray(normalRoots) ? normalRoots : [];
    if (rootRenderLimit >= list.length) return list;
    return list.slice(0, rootRenderLimit);
  }, [normalRoots, rootRenderLimit]);

  const canLoadMoreRoots = useMemo(() => {
    return !isSearchMode && (rootRenderLimit < (normalRoots?.length || 0));
  }, [isSearchMode, rootRenderLimit, normalRoots?.length]);

  const loadMoreRoots = () => {
    setRootRenderLimit((p) => Math.min((normalRoots?.length || 0) + 1, (p || 0) + 80));
  };

  return (
    <div
      style={{
        minHeight: embedded ? 0 : '100vh',
        background: '#f5f7fa',
        // ✅ 모달(embedded)에서는 바깥 박스 스크롤을 없애고, 내부 리스트만 스크롤되게 함
        height: embedded ? '100%' : undefined,
        overflow: embedded ? 'hidden' : undefined,
        display: embedded ? 'flex' : undefined,
        flexDirection: embedded ? 'column' : undefined,
      }}
    >
      {!embedded ? (
        <Header />
      ) : (!selectMode ? (
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid rgba(15,23,42,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 2,
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 950, color: 'var(--mrt-text-2)' }}>🗂️ 그룹 트리(보기 전용)</div>
          <button
            type="button"
            onClick={() => { try { onClose?.(); } catch (e) { /* ignore */ } }}
            style={{
              height: '30px',
              padding: '0 10px',
              borderRadius: '10px',
              border: '1px solid var(--mrt-border)',
              background: 'var(--mrt-surface)',
              cursor: 'pointer',
              fontWeight: 850,
            }}
          >
            닫기
          </button>
        </div>
      ) : null)}
      <div
        style={{
          maxWidth: '1100px',
          margin: '0 auto',
          padding: embedded ? '12px 12px' : '16px 14px',
          flex: embedded ? '1 1 auto' : undefined,
          minHeight: embedded ? 0 : undefined,
          overflow: embedded ? 'hidden' : undefined,
          width: '100%',
          // ✅ 내부에서 flex(리스트 높이 배분)가 동작하도록 컬럼 레이아웃으로 전환
          display: embedded ? 'flex' : undefined,
          flexDirection: embedded ? 'column' : undefined,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '12px' }}>
          <div style={{ fontSize: '18px', fontWeight: 950, color: 'var(--mrt-text)' }}>{embedded ? '그룹 트리' : '🗂️ 그룹 트리(보기 전용)'}</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {selectMode ? (
              <button
                type="button"
                onClick={() => {
                  if (selectedSourceSeq == null) return;
                  try {
                    const seq = Number(selectedSourceSeq);
                    const node = getNode(selectedSourceSeq);
                    const node_path = getNodePath(selectedSourceSeq);
                    onSelectNode?.({ seq, node, node_path });
                  } catch (e) { /* ignore */ }
                  try { onClose?.(); } catch (e) { /* ignore */ }
                }}
                disabled={selectedSourceSeq == null}
                style={{
                  height: '34px',
                  padding: '0 12px',
                  borderRadius: '10px',
                  border: '1px solid rgba(59,130,246,0.30)',
                  background: 'rgba(59,130,246,0.92)',
                  cursor: selectedSourceSeq == null ? 'not-allowed' : 'pointer',
                  fontWeight: 950,
                  color: '#fff',
                  opacity: selectedSourceSeq == null ? 0.55 : 1,
                }}
                title={selectedSourceSeq == null ? '트리에서 그룹을 선택하세요' : '선택한 그룹을 적용'}
              >
                선택
              </button>
            ) : null}
            {!selectMode ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setMoveError('');
                    setMoveSuccess('');
                    if (moveMode) {
                      setMoveMode(false);
                      setMoveTargetSeq(null);
                      setMoveConfirmOpen(false);
                    } else {
                      if (selectedSourceSeq == null) return;
                      setMoveMode(true);
                      setMoveTargetSeq(null);
                    }
                  }}
                  disabled={moveWorking || (!moveMode && selectedSourceSeq == null)}
                  style={{
                    height: '34px',
                    padding: '0 12px',
                    borderRadius: '10px',
                    border: moveMode ? '1px solid rgba(245,158,11,0.35)' : '1px solid var(--mrt-border)',
                    background: moveMode ? 'rgba(245,158,11,0.10)' : 'var(--mrt-surface)',
                    cursor: (!moveMode && selectedSourceSeq == null) ? 'not-allowed' : 'pointer',
                    fontWeight: 950,
                    color: moveMode ? 'rgba(146,64,14,0.95)' : 'var(--mrt-text-2)',
                    opacity: moveWorking ? 0.7 : 1,
                  }}
                  title={selectedSourceSeq == null ? '먼저 이동할 그룹을 선택하세요(트리에서 클릭)' : (moveMode ? '그룹 이동 모드 취소' : '선택한 그룹을 다른 그룹 아래로 이동')}
                >
                  {moveMode ? '이동 취소' : '그룹 이동'}
                </button>
                <button
                  type="button"
                  onClick={() => setMoveConfirmOpen(true)}
                  disabled={moveWorking || !moveMode || selectedSourceSeq == null || moveTargetSeq == null}
                  style={{
                    height: '34px',
                    padding: '0 12px',
                    borderRadius: '10px',
                    border: '1px solid rgba(16,185,129,0.30)',
                    background: 'rgba(16,185,129,0.92)',
                    cursor: (!moveMode || selectedSourceSeq == null || moveTargetSeq == null) ? 'not-allowed' : 'pointer',
                    fontWeight: 950,
                    color: '#fff',
                    opacity: (moveWorking || !moveMode || selectedSourceSeq == null || moveTargetSeq == null) ? 0.6 : 1,
                  }}
                  title={moveMode ? '선택한 대상 그룹 아래로 이동' : '그룹 이동 모드에서만 가능'}
                >
                  이동 실행
                </button>
              </>
            ) : null}
            <button type="button" onClick={load} style={{ height: '34px', padding: '0 12px', borderRadius: '10px', border: '1px solid var(--mrt-border)', background: 'var(--mrt-surface)', cursor: 'pointer', fontWeight: 850 }}>
              새로고침
            </button>
            <button type="button" onClick={expandAllVisible} style={{ height: '34px', padding: '0 12px', borderRadius: '10px', border: '1px solid var(--mrt-border)', background: 'var(--mrt-surface)', cursor: 'pointer', fontWeight: 850 }}>
              모두 펼치기
            </button>
            <button type="button" onClick={collapseAll} style={{ height: '34px', padding: '0 12px', borderRadius: '10px', border: '1px solid var(--mrt-border)', background: 'var(--mrt-surface)', cursor: 'pointer', fontWeight: 850 }}>
              모두 접기
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            flex: embedded ? '1 1 0' : undefined,
            height: embedded ? 0 : undefined,
            minHeight: embedded ? 0 : undefined,
            overflow: embedded ? 'hidden' : undefined,
          }}
        >
          {(selectedSourceSeq != null || moveMode || moveError || moveSuccess) ? (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '12px',
                border: moveError ? '1px solid rgba(239,68,68,0.35)' : '1px solid rgba(148,163,184,0.35)',
                background: moveError ? 'rgba(239,68,68,0.06)' : 'rgba(2,6,23,0.02)',
                display: 'grid',
                gap: '6px',
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: 950, color: 'var(--mrt-text-2)' }}>
                {moveMode ? '그룹 이동 모드' : '선택 정보'}
              </div>
              {selectedSourceSeq != null ? (
                <div style={{ fontSize: '12px', fontWeight: 850, color: 'rgba(146,64,14,0.95)' }}>
                  이동할 그룹: {getNodeLabel(selectedSourceSeq)}
                </div>
              ) : null}
              {moveMode ? (
                <div style={{ fontSize: '12px', fontWeight: 850, color: 'rgba(5,150,105,0.95)' }}>
                  이동할 대상 그룹: {moveTargetSeq != null ? getNodeLabel(moveTargetSeq) : '트리에서 클릭해서 선택하세요'}
                </div>
              ) : null}
              {moveSuccess ? (
                <div style={{ fontSize: '12px', fontWeight: 850, color: 'rgba(5,150,105,0.95)' }}>
                  ✅ {String(moveSuccess)}
                </div>
              ) : null}
              {moveError ? (
                <div style={{ fontSize: '12px', fontWeight: 850, color: 'var(--mrt-danger)' }}>
                  ⚠️ {String(moveError)}
                </div>
              ) : null}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              ref={qInputRef}
              defaultValue={qApplied}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  try { e.preventDefault(); } catch (_e) { /* ignore */ }
                  const next = String(e.currentTarget?.value || '');
                  const isSame = String(qApplied || '') === next;
                  if (!isSame) {
                    setQApplied(next);
                    return;
                  }
                  // ✅ 같은 검색어에서 엔터 = 결과 순환 + "다시 찾기" (결과 1개여도 재스크롤)
                  if (!hitIds.length) return;
                  setHitScrollTick((t) => t + 1);
                  const nextCursor = (hitCursor + 1) % hitIds.length;
                  setHitCursor(nextCursor);
                  setActiveHitId(hitIds[nextCursor]);
                }
              }}
              placeholder="검색: 그룹명 / 그룹ID"
              style={{
                flex: '1 1 320px',
                height: '36px',
                borderRadius: '12px',
                border: '1px solid var(--mrt-border)',
                background: 'var(--mrt-surface)',
                padding: '0 12px',
                fontWeight: 750,
              }}
            />
            <button
              type="button"
              onClick={() => {
                try {
                  setQApplied(String(qInputRef.current?.value || ''));
                } catch (e) {
                  setQApplied('');
                }
              }}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '12px',
                border: '1px solid var(--mrt-border)',
                background: 'var(--mrt-surface)',
                cursor: 'pointer',
                fontWeight: 950,
                color: 'var(--mrt-text-2)',
              }}
              title="엔터와 동일하게 검색을 적용합니다"
            >
              검색
            </button>
            <div style={{ fontSize: '12px', fontWeight: 750, color: 'var(--mrt-muted)' }}>
              총 {Number(tree?.count || 0).toLocaleString()}개
            </div>
            {isSearchMode ? (
              <div style={{ fontSize: '12px', fontWeight: 900, color: 'var(--mrt-text-2)' }}>
                검색결과 {Number(hitIds.length || 0).toLocaleString()}개
                {hitIds.length ? (
                  <span style={{ marginLeft: '6px', fontWeight: 800, color: 'var(--mrt-muted)' }}>
                    ({Math.min(hitCursor + 1, hitIds.length)}/{hitIds.length})
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {loading ? (
            <div style={{ padding: '14px', borderRadius: '12px', border: '1px solid var(--mrt-border)', background: 'var(--mrt-surface)', fontSize: '13px', fontWeight: 800, color: 'var(--mrt-muted)' }}>
              조회 중...
            </div>
          ) : error ? (
            <div style={{ padding: '14px', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.06)', fontSize: '13px', fontWeight: 900, color: 'var(--mrt-danger)' }}>
              ⚠️ {String(error)}
            </div>
          ) : (
            <div
              style={{
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid var(--mrt-border)',
                background: 'var(--mrt-surface)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                flex: embedded ? '1 1 0' : undefined,
                height: embedded ? 0 : undefined,
                minHeight: embedded ? 0 : undefined,
                overflow: embedded ? 'hidden' : undefined,
              }}
            >
              <div
                ref={scrollBoxRef}
                style={{
                  maxHeight: embedded ? undefined : `${listHeight}px`,
                  overflow: 'auto',
                  display: 'grid',
                  gap: '6px',
                  paddingRight: '2px',
                  flex: embedded ? '1 1 0' : undefined,
                  height: embedded ? 0 : undefined,
                  minHeight: embedded ? 0 : undefined,
                  overscrollBehavior: 'contain',
                  alignContent: 'start',
                }}
                onScroll={(e) => {
                  if (!canLoadMoreRoots) return;
                  const el = e.currentTarget;
                  const remain = (el.scrollHeight - (el.scrollTop + el.clientHeight));
                  if (remain < 220) loadMoreRoots();
                }}
              >
                {rootsToRender.length ? rootsToRender.map((r) => renderTreeNode(r, 0, false)) : (
                  <div style={{ fontSize: '13px', fontWeight: 850, color: 'var(--mrt-muted)' }}>
                    {String(qApplied || '').trim() ? '검색 결과가 없습니다.' : '표시할 루트 그룹이 없습니다.'}
                  </div>
                )}
                {canLoadMoreRoots ? (
                  <button
                    type="button"
                    onClick={loadMoreRoots}
                    style={{
                      height: '36px',
                      borderRadius: '12px',
                      border: '1px solid var(--mrt-border)',
                      background: 'var(--mrt-surface)',
                      cursor: 'pointer',
                      fontWeight: 900,
                      color: 'var(--mrt-text-2)',
                    }}
                    title="더 많은 루트 그룹을 렌더링"
                  >
                    더 보기
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
 
      <MrtConfirmDialog
        open={!!moveConfirmOpen}
        ariaLabel="그룹 이동"
        title="그룹 이동"
        tone="info"
        description="선택한 그룹(및 하위 그룹 전체)을 대상 그룹 아래로 이동합니다."
        detail={(selectedSourceSeq != null && moveTargetSeq != null)
          ? `이동할 그룹: ${getNodeLabel(selectedSourceSeq)}\n대상 그룹: ${getNodeLabel(moveTargetSeq)}`
          : ''}
        error={moveError}
        working={moveWorking}
        cancelText="취소"
        confirmText="이동"
        onCancel={() => { if (!moveWorking) setMoveConfirmOpen(false); }}
        onConfirm={async () => {
          if (moveWorking) return;
          if (!moveMode || selectedSourceSeq == null || moveTargetSeq == null) return;
          setMoveWorking(true);
          setMoveError('');
          setMoveSuccess('');
          try {
            const url = `${apiUrl || ''}/api/solar_node/move`;
            const res = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sourceSeq: Number(selectedSourceSeq), targetParentSeq: Number(moveTargetSeq) }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.success) throw new Error(data?.detail || `move_failed:${res.status}`);
            setMoveConfirmOpen(false);
            setMoveMode(false);
            setMoveTargetSeq(null);
            setMoveSuccess('이동이 완료되었습니다.');
            await load();
          } catch (e) {
            setMoveError(String(e?.message || e || 'move_failed'));
          } finally {
            setMoveWorking(false);
          }
        }}
      />

      <MrtDialog
        open={!!addOpen}
        ariaLabel="그룹 추가"
        onClose={() => { if (!addWorking) setAddOpen(false); }}
        zIndex={34000}
        panelStyle={{ width: 'min(420px, 92vw)' }}
      >
        {/* ✅ 모달이 열릴 때 입력에 포커스 */}
        {addOpen ? (
          <div
            style={{ display: 'none' }}
            ref={(el) => {
              if (!el) return;
              setTimeout(() => { try { addInputRef.current?.focus?.(); } catch (_e) { /* ignore */ } }, 0);
            }}
          />
        ) : null}
        <div style={{ padding: '12px 14px', fontWeight: 950, borderBottom: '1px solid var(--mrt-border)', background: 'rgba(59,130,246,0.08)', color: 'rgba(29,78,216,0.98)' }}>
          그룹 추가
        </div>
        <div style={{ padding: '12px 14px', display: 'grid', gap: '10px' }}>
          <div style={{ fontSize: '12px', fontWeight: 850, color: 'var(--mrt-text-2)' }}>
            부모 그룹: {String(addParentLabel || '').trim() || '-'}
          </div>
          <div style={{ display: 'grid', gap: '6px' }}>
            <div style={{ fontSize: '12px', fontWeight: 900, color: 'var(--mrt-text-2)' }}>그룹명</div>
            <input
              key={`addname-${addInputKey}`}
              ref={addInputRef}
              defaultValue=""
              onChange={(e) => {
                addNameRef.current = String(e.target.value || '');
                // 이름이 바뀌면 중복확인 상태 리셋
                if (addDupChecked || addDupOk || addDupMsg) {
                  setAddDupChecked(false);
                  setAddDupOk(false);
                  setAddDupMsg('');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  try { e.preventDefault(); } catch (_e) { /* ignore */ }
                  // 엔터는 "중복확인"으로 동작
                  try { document.getElementById('gt-add-dupcheck-btn')?.click?.(); } catch (_e) { /* ignore */ }
                }
              }}
              placeholder="예) 신규그룹"
              disabled={addWorking}
              style={{ height: '36px', borderRadius: '12px', border: '1px solid var(--mrt-border)', background: 'var(--mrt-surface)', padding: '0 12px', fontWeight: 800 }}
            />
          </div>
          {addDupMsg ? (
            <div style={{ fontSize: '12px', fontWeight: 900, color: addDupOk ? 'rgba(16,185,129,0.98)' : 'rgba(239,68,68,0.98)' }}>
              {String(addDupMsg)}
            </div>
          ) : null}
          {addError ? (
            <div style={{ fontSize: '12px', fontWeight: 850, color: 'var(--mrt-danger)' }}>⚠️ {String(addError)}</div>
          ) : null}
        </div>
        <div style={{ padding: '12px 14px', display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid var(--mrt-border)' }}>
          <button
            type="button"
            id="gt-add-dupcheck-btn"
            onClick={async () => {
              if (addDupWorking || addWorking) return;
              const p = Number(addParentSeq);
              const nm = String((addInputRef.current?.value ?? addNameRef.current) || '').trim();
              if (!p || !nm) {
                setAddDupChecked(false);
                setAddDupOk(false);
                setAddDupMsg('그룹명을 입력하세요.');
                return;
              }
              setAddDupWorking(true);
              setAddDupMsg('');
              try {
                const url = `${apiUrl || ''}/api/solar_node/child_name_check`;
                const res = await fetch(url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ parentSeq: p, name: nm }),
                });
                const json = await res.json().catch(() => ({}));
                if (!res.ok || !json?.success) throw new Error(json?.detail || `dup_check_failed:${res.status}`);
                const ok = !!json?.data?.available;
                setAddDupChecked(true);
                setAddDupOk(ok);
                setAddDupMsg(ok ? '사용 가능한 그룹명입니다.' : '이미 존재하는 그룹명입니다.');
              } catch (e) {
                setAddDupChecked(false);
                setAddDupOk(false);
                setAddDupMsg(String(e?.message || e || 'dup_check_failed'));
              } finally {
                setAddDupWorking(false);
              }
            }}
            disabled={addWorking || !addParentSeq || addDupWorking}
            style={{ height: '34px', padding: '0 12px', borderRadius: '12px', border: '1px solid var(--mrt-border)', background: 'var(--mrt-surface)', color: 'var(--mrt-text-2)', fontWeight: 900, cursor: 'pointer', opacity: (addWorking || !addParentSeq) ? 0.6 : 1 }}
          >
            {addDupWorking ? '확인 중…' : '중복확인'}
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(false)}
            disabled={addWorking}
            style={{ height: '34px', padding: '0 12px', borderRadius: '12px', border: '1px solid var(--mrt-border)', background: 'var(--mrt-surface)', color: 'var(--mrt-text-2)', fontWeight: 900, cursor: 'pointer', opacity: addWorking ? 0.7 : 1 }}
          >
            취소
          </button>
          <button
            type="button"
            id="gt-add-confirm-btn"
            disabled={addWorking || !addParentSeq || !addDupChecked || !addDupOk}
            onClick={async () => {
              const p = Number(addParentSeq);
              const nm = String((addInputRef.current?.value ?? addNameRef.current) || '').trim();
              if (!p || !nm) return;
              setAddWorking(true);
              setAddError('');
              try {
                const url = `${apiUrl || ''}/api/solar_node/create_child`;
                const res = await fetch(url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ parentSeq: p, name: nm }),
                });
                const json = await res.json().catch(() => ({}));
                if (!res.ok || !json?.success) {
                  const detail = String(json?.detail || `create_child_failed:${res.status}`);
                  if (detail.includes('duplicate_node_name')) {
                    setAddDupChecked(true);
                    setAddDupOk(false);
                    setAddDupMsg('이미 존재하는 그룹명입니다.');
                  }
                  throw new Error(detail);
                }
                setAddOpen(false);
                await load();
                // 부모는 펼쳐두기
                setUserExpanded((prev) => {
                  const next = new Set(prev);
                  next.add(p);
                  return next;
                });
              } catch (e) {
                setAddError(String(e?.message || e || 'create_child_failed'));
              } finally {
                setAddWorking(false);
              }
            }}
            style={{ height: '34px', padding: '0 12px', borderRadius: '12px', border: '1px solid rgba(59,130,246,0.30)', background: 'rgba(59,130,246,0.92)', color: '#fff', fontWeight: 950, cursor: 'pointer', opacity: (addWorking || !addParentSeq) ? 0.6 : 1 }}
          >
            {addWorking ? '생성 중…' : '생성'}
          </button>
        </div>
      </MrtDialog>
    </div>
  );
}


